import React, { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faEnvelope, faGift, faTrophy } from '@fortawesome/free-solid-svg-icons';
import AuthenticateService from './AuthenticateService';
import Sidebar from './Sidebar';
import Header from './Header';
import Footer from './Footer';
import PreLoader from './extra/PreLoader';

const money = (n) => `₦${(parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Referral = () => {
  const { http, user, logout } = AuthenticateService();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({});
  const [earnings, setEarnings] = useState([]);

  useEffect(() => {
    document.title = 'Refer & Earn';
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, earningsRes] = await Promise.all([
        http.get('/referral/summary'),
        http.get('/referral/earnings'),
      ]);
      if (summaryRes.data.status === true) {
        setData(summaryRes.data.data || {});
        setEarnings(earningsRes.data.status === true ? (earningsRes.data.data || []) : []);
      } else {
        setError(summaryRes.data.message || 'Referral is not available right now.');
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        return;
      }
      setError('Referral is not available right now. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const code = data.referral_code || '';
  const link = data.referral_link || '';
  const shareText = `Join me on Agrospace! Sign up with my referral code ${code} and start farming smarter: ${link}`;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch (err) {
      toast.error('Could not copy to clipboard');
    }
  };

  const stats = data.stats || {};
  const bonus = data.bonus || {};
  const referrals = data.referrals || [];
  const tiers = bonus.tiers || [];
  const active = parseInt(stats.active_this_month, 10) || 0;
  const nextTierTarget = bonus.next_tier ? parseInt(bonus.next_tier.min_active, 10) || 1 : 1;
  const progress = Math.min(100, Math.round((active / nextTierTarget) * 100));

  return (
    <>
      {loading ? (
        <PreLoader />
      ) : (
        <>
          <Sidebar />
          <Header user={user} />

          <div className="content">
            <h4 className="fs-1 mb-4">Refer & Earn</h4>

            {error ? (
              <div className="text-center py-5">
                <p className="text-700">{error}</p>
                <button className="btn btn-primary" onClick={load}>Try again</button>
              </div>
            ) : (
              <>
                {data.program_enabled !== true && (
                  <div className="alert alert-warning">
                    The referral reward is not switched on yet, but your link is ready to share.
                  </div>
                )}
                {data.how_it_works && <p className="fs-0 mb-4">{data.how_it_works}</p>}

                <div className="row g-4">
                  <div className="col-12 col-lg-7">
                    <div className="card mb-4">
                      <div className="card-body">
                        <div className="text-500 mb-1">Your referral code</div>
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <span className="fs-3 fw-bold" style={{ letterSpacing: '1px' }}>{code}</span>
                          <button className="btn btn-outline-primary btn-sm" onClick={() => copy(code, 'Code')}>
                            <FontAwesomeIcon icon={faCopy} className="me-1" /> Copy
                          </button>
                        </div>
                        <hr />
                        <div className="text-500 mb-1">Your referral link</div>
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <span className="text-truncate me-2">{link}</span>
                          <button className="btn btn-outline-primary btn-sm flex-shrink-0" onClick={() => copy(link, 'Link')}>
                            <FontAwesomeIcon icon={faCopy} className="me-1" /> Copy
                          </button>
                        </div>
                        <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
                          <a
                            className="btn btn-success"
                            href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <i className="fab fa-whatsapp me-2" aria-hidden="true"></i> WhatsApp
                          </a>
                          <a className="btn btn-outline-secondary" href={`mailto:?subject=${encodeURIComponent('Join me on Agrospace')}&body=${encodeURIComponent(shareText)}`}>
                            <FontAwesomeIcon icon={faEnvelope} className="me-2" /> Email
                          </a>
                          <button className="btn btn-outline-secondary" onClick={() => copy(shareText, 'Invite message')}>
                            <FontAwesomeIcon icon={faCopy} className="me-2" /> Copy Invite
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="row g-3 mb-4">
                      <div className="col-6 col-md-3">
                        <div className="card text-center h-100"><div className="card-body py-3">
                          <div className="fs-2 fw-bold">{stats.total_referred || 0}</div>
                          <div className="text-500 fs--1">Referred</div>
                        </div></div>
                      </div>
                      <div className="col-6 col-md-3">
                        <div className="card text-center h-100"><div className="card-body py-3">
                          <div className="fs-2 fw-bold">{stats.qualified || 0}</div>
                          <div className="text-500 fs--1">Qualified</div>
                        </div></div>
                      </div>
                      <div className="col-6 col-md-3">
                        <div className="card text-center h-100"><div className="card-body py-3">
                          <div className="fs-2 fw-bold text-primary">{money(stats.total_earned)}</div>
                          <div className="text-500 fs--1">Earned</div>
                        </div></div>
                      </div>
                      <div className="col-6 col-md-3">
                        <div className="card text-center h-100"><div className="card-body py-3">
                          <div className="fs-2 fw-bold text-warning">{money(stats.awaiting_approval)}</div>
                          <div className="text-500 fs--1">Awaiting Approval</div>
                        </div></div>
                      </div>
                    </div>

                    {bonus.enabled && (
                      <div className="card mb-4">
                        <div className="card-body">
                          <h6 className="mb-2"><FontAwesomeIcon icon={faTrophy} className="text-warning me-2" />Monthly Top-Referrer Bonus</h6>
                          <p className="mb-2">{active} of your referrals transacted this month.</p>
                          {bonus.current_tier && (
                            <p className="text-success mb-2">You are on track for {money(bonus.current_tier.amount)} at month end.</p>
                          )}
                          {bonus.next_tier && (
                            <>
                              <div className="progress mb-2" style={{ height: 8 }}>
                                <div className="progress-bar" role="progressbar" style={{ width: `${progress}%` }} />
                              </div>
                              <div className="text-500 fs--1 mb-3">
                                {Math.max(0, nextTierTarget - active)} more active referral(s) to earn {money(bonus.next_tier.amount)}.
                              </div>
                            </>
                          )}
                          <div className="d-flex flex-wrap" style={{ gap: '6px' }}>
                            {tiers.map((t, i) => (
                              <span key={i} className="badge bg-light text-dark border">
                                {t.min_active}+ active → {money(t.amount)}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {referrals.length > 0 && (
                      <div className="card">
                        <div className="card-body">
                          <h6 className="mb-3">People You Referred</h6>
                          {referrals.map((r, i) => (
                            <div key={i} className="d-flex justify-content-between align-items-center border-bottom py-2">
                              <div>
                                <div className="fw-semi-bold">{r.name}</div>
                                <div className="text-500 fs--1">Joined {r.joined_at || ''}</div>
                              </div>
                              <span className={`badge ${r.qualified ? 'bg-success' : 'bg-warning'}`}>
                                {r.qualified ? 'Qualified' : 'Waiting'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="col-12 col-lg-5">
                    <div className="card">
                      <div className="card-body">
                        <h6 className="mb-3">Earnings</h6>
                        {earnings.length === 0 ? (
                          <p className="text-500">No earnings yet.</p>
                        ) : (
                          earnings.map((e) => (
                            <div key={e.id} className="d-flex align-items-center border-bottom py-2">
                              <FontAwesomeIcon icon={faGift} className="text-warning me-3" />
                              <div className="flex-grow-1">
                                <div className="fw-semi-bold">
                                  {e.type === 'bonus' ? `Monthly bonus ${e.period || ''}` : `Referral: ${e.referee || ''}`}
                                </div>
                                <div className="text-500 fs--1">
                                  {e.date ? new Date(e.date).toLocaleDateString() : ''} · {e.status === 'paid' ? 'Paid' : 'Awaiting approval'}
                                </div>
                              </div>
                              <div className="fw-bold">{money(e.amount)}</div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            <Footer />
          </div>

          <ToastContainer />
        </>
      )}
    </>
  );
};

export default Referral;
